import { useState } from 'react';
import ConfiguracoesContaIdioma from './Idioma';
import ConfiguracoesContaNotifications from './Notifications';
import ConfiguracoesContaGeral from './conta/Geral';
import ConfiguracoesContaEmail from './conta/Email';
import ConfiguracoesContaPhoneNumber from './conta/PhoneNumber';
import ConfiguracoesContaPassword from './conta/Password';
import { useAuthContext } from '../../../contexts/AuthContext';
import AvatarUser from '../../../components/AvatarUser';
import { CONNECT_FLAG, GoogleCalendarIntegration } from './GoogleCalendarIntegration';
import { SectionIcon, SettingsNav, SettingsNavItem } from '../estabelecimento/Form/SettingsGroup';
import { Bell, ChevronLeft, Globe, Lock, Mail, MessageCircle, UserRound } from 'lucide-react';
import { GoogleIcon } from '../../../components/BrandIcons';

type SectionKey = 'perfil' | 'email' | 'whatsapp' | 'senha' | 'idioma' | 'notificacoes' | 'google';

// O retorno do OAuth volta para esta rota e deve abrir a secao que iniciou a conexao.
const isGoogleCalendarReturn = () =>
    new URLSearchParams(window.location.search).get('googleCalendar') === '1' ||
    sessionStorage.getItem(CONNECT_FLAG) === '1';

export default function ConfiguracoesConta() {
    const { user } = useAuthContext();
    const userName = user?.displayName || user?.email || 'Conta';
    const userEmail = user?.email || 'E-mail não informado';
    const roles = [
        user?.root && 'Root',
        user?.admin && 'Admin',
        user?.worker && 'Atendente',
        user?.client && 'Cliente',
    ].filter(Boolean) as string[];
    const canUseGoogleCalendar = !!(user?.worker || user?.admin || user?.root);

    const sections: SettingsNavItem<SectionKey>[] = [
        {
            key: 'perfil',
            title: 'Perfil',
            description: 'Foto e nome',
            icon: UserRound,
        },
        {
            key: 'email',
            title: 'E-mail',
            description: userEmail,
            icon: Mail,
        },
        {
            key: 'whatsapp',
            title: 'WhatsApp',
            description: user?.phoneNumber || 'Alertas e confirmações',
            icon: MessageCircle,
            trailing: !user?.phoneNumber ? (
                <span className="shrink-0 text-xs font-medium text-warning">Pendente</span>
            ) : undefined,
        },
        {
            key: 'senha',
            title: 'Senha',
            description: 'Segurança da conta',
            icon: Lock,
        },
        {
            key: 'idioma',
            title: 'Idioma',
            description: 'Idioma preferido',
            icon: Globe,
        },
        {
            key: 'notificacoes',
            title: 'Notificações',
            description: 'E-mail, push e este dispositivo',
            icon: Bell,
        },
        ...(canUseGoogleCalendar
            ? [
                  {
                      key: 'google' as const,
                      title: 'Google Calendar',
                      description: 'Sincronização dos agendamentos',
                      icon: GoogleIcon,
                  },
              ]
            : []),
    ];

    const [activeKey, setActiveKey] = useState<SectionKey>(() => (isGoogleCalendarReturn() ? 'google' : 'perfil'));
    // No mobile a navegacao e em dois niveis: lista de secoes e detalhe da secao.
    const [mobileDetailOpen, setMobileDetailOpen] = useState(isGoogleCalendarReturn);
    const active = sections.find((section) => section.key === activeKey) || sections[0];

    const openSection = (key: SectionKey) => {
        setActiveKey(key);
        setMobileDetailOpen(true);
        window.scrollTo({ top: 0 });
    };

    const sectionPanel = (key: SectionKey) => (active.key === key ? 'block' : 'hidden');

    return (
        <div className="estab-settings w-full lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start lg:gap-8 xl:grid-cols-[18.5rem_minmax(0,1fr)] xl:gap-10">
            <aside className={`${mobileDetailOpen ? 'hidden lg:block' : 'block'} space-y-6 lg:sticky lg:top-20`}>
                <div className="estab-group flex items-center gap-4 p-4 lg:gap-3 lg:p-3">
                    <div className="lg:hidden">
                        <AvatarUser name={userName} photoURL={user?.photoURL || undefined} size="h-16 w-16 !text-xl" />
                    </div>
                    <div className="hidden lg:block">
                        <AvatarUser name={userName} photoURL={user?.photoURL || undefined} size="h-11 w-11 !text-sm" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="break-words text-[1.0625rem] font-semibold leading-snug text-base-content lg:text-sm">
                            {userName}
                        </div>
                        <div className="break-words text-sm text-base-content/60 lg:text-xs">{userEmail}</div>
                        {roles.length > 0 && (
                            <div className="text-xs text-base-content/50">{roles.join(' · ')}</div>
                        )}
                    </div>
                </div>

                <SettingsNav
                    label="Seções da conta"
                    items={sections}
                    activeKey={active.key}
                    onOpen={openSection}
                    onSelect={setActiveKey}
                />
            </aside>

            {/* Cada secao tem o proprio formulario; ficam montadas para preservar estado e o retorno do OAuth. */}
            <div className={`${mobileDetailOpen ? 'block' : 'hidden lg:block'} min-w-0`}>
                <div className="mb-5 space-y-2 lg:mb-6">
                    <button
                        type="button"
                        className="sf-back-button -ml-1 lg:hidden"
                        onClick={() => setMobileDetailOpen(false)}
                    >
                        <ChevronLeft aria-hidden="true" />
                        Conta
                    </button>
                    <div className="flex items-center gap-3">
                        <SectionIcon icon={active.icon} className="text-base-content/45" />
                        <h2 className="text-[1.375rem] font-bold tracking-tight text-base-content lg:text-xl">
                            {active.title}
                        </h2>
                    </div>
                </div>

                <div className={sectionPanel('perfil')}>
                    <ConfiguracoesContaGeral />
                </div>

                <div className={sectionPanel('email')}>
                    <ConfiguracoesContaEmail />
                </div>

                <div className={sectionPanel('whatsapp')}>
                    <ConfiguracoesContaPhoneNumber />
                </div>

                <div className={sectionPanel('senha')}>
                    <ConfiguracoesContaPassword />
                </div>

                <div className={sectionPanel('idioma')}>
                    <ConfiguracoesContaIdioma />
                </div>

                <div className={sectionPanel('notificacoes')}>
                    <ConfiguracoesContaNotifications />
                </div>

                {canUseGoogleCalendar && (
                    <div className={sectionPanel('google')}>
                        <GoogleCalendarIntegration />
                    </div>
                )}
            </div>
        </div>
    );
}
